import { useEffect, useState, MutableRef } from 'preact/hooks';
import { useFloating, autoUpdate, shift, offset, flip, useInteractions, useClientPoint } from '@floating-ui/react';

// Check if the device supports hover (evaluated once).
const canHover = window.matchMedia( '(hover: hover)' ).matches;

type UseFloatingTooltipProps = {
	wrapperRef: MutableRef< HTMLDivElement | null >;
	locations: MapLocation[];
};

/**
 * Parse the location index from an element or its closest ancestor with a data-location-index attribute.
 *
 * @param {Element | null} element - The element to check.
 * @return {number | null} The parsed index, or null if not found.
 */
function getLocationIndex( element: Element | null ): number | null {
	const pointElement = element?.closest( '[data-location-index]' );
	if ( ! pointElement ) {
		return null;
	}

	const index = parseInt( pointElement.getAttribute( 'data-location-index' ) || '', 10 );
	return isNaN( index ) ? null : index;
}

/**
 * Hook for floating tooltip display on location points.
 * Tracks hover and focus state and provides Floating UI positioning.
 *
 * @param {Object}           props             - Hook props.
 * @param {MutableRef}       props.wrapperRef       - Ref to the element listening for hover events.
 * @param {MapLocation[]}    props.locations        - Array of map locations.
 */
export default function useFloatingTooltip( { wrapperRef, locations }: UseFloatingTooltipProps ) {
	const [ hoveredIndex, setHoveredIndex ] = useState< number | null >( null );
	const [ focusedIndex, setFocusedIndex ] = useState< number | null >( null );

	// Show tooltip on hover (desktop) or keyboard focus.
	const activeIndex = hoveredIndex ?? focusedIndex;
	const showTooltip = activeIndex !== null && !! locations[ activeIndex ];

	// Resolve the boundary element for Floating UI middleware.
	// Read from the ref on each render so flip/shift pick up the mounted element.
	const boundary = wrapperRef.current || undefined;

	// Set up Floating UI.
	const { refs: floatingRefs, floatingStyles, context, isPositioned, placement } = useFloating( {
		whileElementsMounted: autoUpdate,
		placement: 'bottom-start',
		strategy: 'absolute',
		middleware: [
			offset( { mainAxis: 4, crossAxis: 6 } ),
			flip( { boundary } ),
			shift( { boundary, padding: 8 } ),
		],
	} );

	// Track mouse position for tooltip anchor (only for hover, not keyboard focus).
	const clientPoint = useClientPoint( context, {
		enabled: canHover && hoveredIndex !== null,
	} );

	// Set up the interactions for Floating UI.
	const { getReferenceProps, getFloatingProps } = useInteractions( [
		clientPoint,
	] );

	// Listen for hover, focus, and keyboard events on the wrapper element.
	// Reference switching is done synchronously in handlers (not useEffect)
	// so Floating UI repositions immediately and avoids stale useClientPoint positions.
	useEffect( () => {
		const wrapper = wrapperRef.current;

		if ( ! wrapper ) {
			return;
		}

		const abortController = new AbortController();
		const { signal } = abortController;

		// Mouse hover events — reset reference to wrapper for useClientPoint tracking.
		wrapper.addEventListener( 'mouseover', ( e ) => {
			const index = getLocationIndex( e.target as Element );
			if ( index !== null ) {
				setHoveredIndex( index );
				setFocusedIndex( null );
				floatingRefs.setReference( wrapper );

				// Blur any focused point so its focus styles don't linger.
				( document.activeElement as HTMLElement )?.blur();
			}
		}, { signal } );

		wrapper.addEventListener( 'mouseout', ( e ) => {
			const relatedTarget = e.relatedTarget as Element | null;

			// Only reset if we've left the wrapper entirely or moved to a non-point element.
			if ( ! relatedTarget || ! relatedTarget.closest( '[data-location-index]' ) ) {
				setHoveredIndex( null );
			}
		}, { signal } );

		// Keyboard focus events — set focused element as reference for positioning.
		// Skip focus events caused by mouse clicks (hoveredIndex is already set).
		wrapper.addEventListener( 'focusin', ( e ) => {
			const target = e.target as Element;
			const pointElement = target.closest( '[data-location-index]' );
			if ( pointElement && ! pointElement.matches( ':hover' ) ) {
				const index = parseInt( pointElement.getAttribute( 'data-location-index' ) || '', 10 );
				if ( ! isNaN( index ) ) {
					setFocusedIndex( index );
					setHoveredIndex( null );

					// Clear the virtual position reference left by useClientPoint
					// so the focused element's position is used instead.
					floatingRefs.setPositionReference( null );
					floatingRefs.setReference( pointElement as HTMLElement );
				}
			}
		}, { signal } );

		wrapper.addEventListener( 'focusout', () => {
			setFocusedIndex( null );
		}, { signal } );

		// Escape dismisses tooltip.
		wrapper.addEventListener( 'keydown', ( e ) => {
			if ( e.key === 'Escape' ) {
				setHoveredIndex( null );
				setFocusedIndex( null );

				// Remove focus to prevent re-triggering.
				( document.activeElement as HTMLElement )?.blur();
			}
		}, { signal } );

		return () => {
			abortController.abort();
		};
	}, [ wrapperRef ] );

	return {
		activeIndex,
		showTooltip,
		floatingStyles,
		floatingRefs,
		getReferenceProps,
		getFloatingProps,
		isPositioned,
		placement,
	};
}
